import { extract_units_without_fstring } from "@utils/format/pythonicFormat"

/**
 * Parameters for generating a unified chart label
 */
export interface ChartLabelParams {
  /**
   * The node name (e.g., "Temperature", "Pressure")
   */
  nodeName: string

  /**
   * The unit format string from the node (e.g., "{.2f} °C", "{.0f} Pa")
   * If provided, the unit will be extracted and added to the label
   */
  nodeUnit?: string

  /**
   * The run name (e.g., "Baseline Run")
   * Only included when numberOfRuns > 1
   */
  runName?: string

  /**
   * The run version (e.g., "1.2.3")
   * Only included when numberOfRuns > 1 and version is provided
   */
  runVersion?: string

  /**
   * The total number of runs being displayed in the chart
   * - If > 1: run name/version will be included in the label
   * - If <= 1: run name/version will be omitted
   */
  numberOfRuns: number

  /**
   * Statistical suffix to append (e.g., "mean", "5th percentile", "μ+3σ")
   */
  statisticalSuffix?: string
}

/**
 * Generates a unified chart label following the format:
 * {NODE_NAME} [{NODE_UNIT}] ({RUN_NAME} {version}) ({statisticalSuffix})
 *
 * - NODE_UNIT is omitted if not provided
 * - RUN_NAME and version are omitted if numberOfRuns <= 1
 * - statisticalSuffix is omitted if not provided
 *
 * @param params - Parameters for label generation
 * @returns Formatted chart label string
 */
export function generateChartLabel(params: ChartLabelParams): string {
  const { nodeName, nodeUnit, runName, runVersion, numberOfRuns, statisticalSuffix } = params

  const parts: string[] = []

  // remove "_" in case of getting node key instead of label
  parts.push(nodeName.replace(/_/g, " "))

  if (nodeUnit) {
    const extractedUnit = extract_units_without_fstring(nodeUnit)
    if (extractedUnit) {
      parts.push(`[${extractedUnit}]`)
    }
  }

  if (numberOfRuns > 1 && runName) {
    const runPart = runVersion ? `${runName} ${runVersion}` : runName
    parts.push(`(${runPart})`)
  }

  if (statisticalSuffix) {
    parts.push(`(${statisticalSuffix})`)
  }

  return parts.join(" ")
}
